import { Injectable } from '@angular/core';
import { FormActionHandlerService } from './base.service';
import { FormShareService } from '../share.service';
import { FormStatusService } from '../status.service';
import { FormDataService } from '../data.service';
import { SwalService } from '../../../fastlane-common/services/swal.service';
import { EmailService } from '../../../email/services/email.service';
import { ProjectDataService } from '../../../project/services/project-data.service';
import { ProjectDetailService } from '../../../project/services/project-shared.service';
import { CastProject } from '../../../project/project.functions';
import { formStatus } from '../../form.constants';
import { projectStatus } from '../../../project/project.constants';
import { StatusLog } from '../../../project/models/status-log.model';
import { UserContextService } from '../../../user/services/user-context.service';
import { SweetAlertOptions } from 'sweetalert2';

@Injectable()
export class DenyRdApplicationActionHandlerService extends FormActionHandlerService {
  constructor(
    private shareService: FormShareService,
    private statusService: FormStatusService,
    private dataService: FormDataService,
    private swal: SwalService,
    private emailService: EmailService,
    private projectDataService: ProjectDataService,
    private projectDetailService: ProjectDetailService,
    private userContext: UserContextService
  ) {
    super();
  }

  exec(): Promise<any> {
    const that = this;

    const denialReasons: {
      key: string;
      title: string;
      description: string;
    }[] = [
      {
        key: 'noIncrementalIncrease',
        title: 'No Incremental Increase in Research and Development Expenses',
        description:
          'The applicant did not demonstrate an incremental increase in qualified research expenditures as required to qualify for the credit.'
      },
      {
        key: 'ineligibleActivities',
        title: 'Ineligible Research Activities',
        description:
          'The activities described do not meet the requirements for qualified research under the Louisiana Research and Development Tax Credit Program.'
      },
      {
        key: 'ineligibleExpenditures',
        title: 'Ineligible Research Expenditures',
        description:
          'The expenditures claimed are not eligible for the credit under applicable statutes and administrative rules.'
      },
      {
        key: 'ineligibleIndustry',
        title: 'Ineligible Industry',
        description:
          'The applicant\'s primary business activity is not eligible for the Louisiana Research and Development Tax Credit Program.'
      },
      {
        key: 'other',
        title: 'Other',
        description: ''
      }
    ];

    const reasonPairs: { [key: string]: string } = {};
    denialReasons.forEach(reason => {
      reasonPairs[reason.key] =
        reason.key === 'other'
          ? '<b>Other</b> (Comments Required)'
          : `<b>${reason.title}</b> — ${reason.description}`;
    });

    return new Promise((resolve, reject) => {
      that.swal
        .radio(
          <SweetAlertOptions>{
            title: 'Deny this application?',
            text: 'Select the reason for denying this application.',
            type: 'warning',
            confirmButtonText: 'Submit',
            allowOutsideClick: false,
            onOpen: (modalElement: HTMLElement) => {
              const radioContainer = modalElement.querySelector(
                '.swal2-radio'
              ) as HTMLElement;
              if (!radioContainer) {
                return;
              }
              radioContainer.style.flexDirection = 'column';
              radioContainer.style.alignItems = 'flex-start';
              radioContainer
                .querySelectorAll('label')
                .forEach((element: Element) => {
                  const label = element as HTMLElement;
                  label.style.display = 'flex';
                  label.style.margin = '0.3em 0';
                  label.style.textAlign = 'left';
                  label.style.fontSize = '0.9rem';
                  label.style.lineHeight = '1.3';
                });

              const updateConfirmButton = () => {
                const selected = radioContainer.querySelector(
                  'input:checked'
                ) as HTMLInputElement;
                const confirmButton = modalElement.querySelector(
                  '.swal2-confirm'
                ) as HTMLElement;
                if (confirmButton) {
                  confirmButton.innerHTML =
                    selected && selected.value === 'other'
                      ? 'Continue'
                      : 'Submit';
                }
              };
              radioContainer
                .querySelectorAll('input')
                .forEach((element: Element) => {
                  element.addEventListener('change', updateConfirmButton);
                });
              updateConfirmButton();
            }
          },
          reasonPairs
        )
        .then(selectedKey => {
          const reason = denialReasons.find(r => r.key === selectedKey);

          const gatherComment: Promise<string> =
            selectedKey === 'other'
              ? that.swal.textarea(
                  <SweetAlertOptions>{
                    title: 'Reason for Denial',
                    type: 'warning',
                    confirmButtonText: 'Yes, Deny Application',
                    inputPlaceholder:
                      'Enter the reason for denying this application.',
                    allowOutsideClick: false
                  },
                  'required',
                  'A comment is required when selecting Other.'
                )
              : Promise.resolve('');

          return gatherComment.then(comment => ({ reason, comment }));
        })
        .then((selection: { reason: any; comment: string }) => {
          const reason = selection.reason;
          const comment = (selection.comment || '').trim();

          const reasonTitle = reason.title;
          const reasonDescription =
            reason.key === 'other' ? comment : reason.description;
          const statusComment = reasonDescription
            ? `${reasonTitle} - ${reasonDescription}`
            : reasonTitle;

          that.swal.load('Denying Application...', 'Please wait.');

          that.statusService.changeStatus(formStatus.denied);
          const deniedEntry = that.shareService.form.statuses[0];
          if (deniedEntry) {
            deniedEntry.comment = statusComment;
          }

          that.shareService.project.projectStatusLog.unshift(<StatusLog>{
            creator: that.userContext.currentUser.id,
            status: projectStatus.projectDenied,
            statusDate: new Date(),
            comment: statusComment
          });

          that.dataService.saveForm().subscribe(
            () => {
              const projectGuid = that.shareService.formId.projectGuid;
              that.emailService
                .sendRdApplicationDeniedEmail(
                  that.shareService.project,
                  that.shareService.form,
                  projectGuid,
                  reasonTitle,
                  reasonDescription
                )
                .subscribe(
                  () => {
                    that.swal
                      .success({
                        title: 'Application Denied',
                        text:
                          'The application has been denied and the applicant ' +
                          'and contacts have been notified.'
                      })
                      .then(() => that.pullProject().then(() => resolve()))
                      .catch(() => that.pullProject().then(() => resolve()));
                  },
                  () => {
                    that.swal
                      .error({
                        title:
                          'Application denied, but the notification email could not be sent.',
                        text:
                          'The application is now Denied. Please try notifying the applicant again.'
                      })
                      .then(() => that.pullProject().then(() => resolve()))
                      .catch(() => that.pullProject().then(() => resolve()));
                  }
                );
            },
            errorSaving => {
              if (errorSaving === 409) {
                reject(errorSaving);
              } else {
                reject(
                  'We were unable to deny the application. Please try again later.'
                );
              }
            }
          );
        })
        .catch(() => resolve());
    });
  }

  private pullProject(): Promise<void> {
    const projectGuid = this.shareService.formId.projectGuid;
    return new Promise(resolve => {
      this.projectDataService.getProject(projectGuid).subscribe(
        project => {
          if (project) {
            this.projectDetailService.project = CastProject(
              project,
              project.projectInfo.incentiveProgram
            );
          }
          resolve();
        },
        () => resolve()
      );
    });
  }
}
